"use client";

import { useEffect, useSyncExternalStore } from "react";
import {
  isTimeZoneTableLoaded,
  loadTimeZoneTable,
  subscribeToTimeZoneTable,
  timeZoneForCoords,
} from "~/lib/utils/timeZone";

// The table is never loaded on the server: it is fetched in an effect, so SSR and
// the first client render both see "not loaded" and agree. Hydration therefore
// can't mismatch on the zone — the real value arrives in a later render, the same
// shape as any other client-only reading.
const notLoadedOnServer = () => false;

/**
 * The destination's IANA zone for a coordinate, once the boundary table is in.
 *
 * Wraps `timeZoneForCoords` with the load it now needs (see `loadTimeZoneTable` —
 * the table is a lazily-fetched chunk so that routes without a search form don't
 * pay for it). Returns `undefined` until the table lands, which callers already
 * treat as "use the viewer's zone", then re-renders with the real zone.
 *
 * The load starts on mount rather than when a coordinate appears: a driver can't
 * produce one without a Mapbox round trip first, so starting early means the
 * answer is ready by the time it is asked for, while still costing nothing on a
 * route that never mounts a search form.
 */
export function useDestinationTimeZone(
  lat: number | undefined | null,
  lng: number | undefined | null,
): string | undefined {
  const loaded = useSyncExternalStore(
    subscribeToTimeZoneTable,
    isTimeZoneTableLoaded,
    notLoadedOnServer,
  );

  useEffect(() => {
    void loadTimeZoneTable();
  }, []);

  return loaded ? timeZoneForCoords(lat, lng) : undefined;
}
